import React, { useState, useEffect } from 'react'
import { supabaseClient } from '../config/supabase'
import {
  Briefcase, GraduationCap, Award, Star, Users, Circle,
  Plus, Pencil, Trash2, X, ExternalLink, MapPin, Calendar,
} from 'lucide-react'
import type { CareerTimelineEntry, CareerEntryType } from '../types'
import { SALARY_LABELS } from '../constants/profileOptions'
import toast from 'react-hot-toast'

/* ── Constants ─────────────────────────────────────────────── */

const EMP_TYPE_LABELS: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  internship: 'Internship',
  freelance: 'Freelance',
}

const ENTRY_TYPE_LABELS: Record<CareerEntryType, string> = {
  job: 'Job',
  internship: 'Internship',
  education: 'Education',
  certification: 'Certification',
  achievement: 'Achievement',
  other: 'Other',
}

const ENTRY_COLORS: Record<CareerEntryType, { dot: string; bg: string; text: string }> = {
  job:           { dot: 'bg-blue-600',    bg: 'bg-blue-50',   text: 'text-blue-700'   },
  internship:    { dot: 'bg-purple-500',  bg: 'bg-purple-50', text: 'text-purple-700' },
  education:     { dot: 'bg-emerald-600', bg: 'bg-emerald-50',text: 'text-emerald-700'},
  certification: { dot: 'bg-yellow-500',  bg: 'bg-yellow-50', text: 'text-yellow-700' },
  achievement:   { dot: 'bg-orange-500',  bg: 'bg-orange-50', text: 'text-orange-700' },
  other:         { dot: 'bg-gray-500',    bg: 'bg-gray-100',  text: 'text-gray-600'   },
}

/* ── Helpers ───────────────────────────────────────────────── */

function EntryIcon({ type }: { type: CareerEntryType }) {
  const cls = 'w-4 h-4'
  if (type === 'job')           return <Briefcase className={cls} />
  if (type === 'internship')    return <Users className={cls} />
  if (type === 'education')     return <GraduationCap className={cls} />
  if (type === 'certification') return <Award className={cls} />
  if (type === 'achievement')   return <Star className={cls} />
  return <Circle className={cls} />
}

function formatDateRange(entry: CareerTimelineEntry): string {
  const start = new Date(entry.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  if (entry.is_current) return `${start} – Present`
  if (!entry.end_date) return start
  const end = new Date(entry.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  return `${start} – ${end}`
}

type FormState = {
  entry_type: CareerEntryType
  title: string
  organization: string
  location: string
  start_date: string
  end_date: string
  is_current: boolean
  description: string
  employment_type: string
  salary_range: string
  degree: string
  field_of_study: string
  credential_url: string
}

const BLANK: FormState = {
  entry_type: 'job',
  title: '',
  organization: '',
  location: '',
  start_date: '',
  end_date: '',
  is_current: false,
  description: '',
  employment_type: '',
  salary_range: '',
  degree: '',
  field_of_study: '',
  credential_url: '',
}

/* ── Component ─────────────────────────────────────────────── */

interface Props {
  alumniId: string
  canEdit?: boolean
}

const CareerTimeline: React.FC<Props> = ({ alumniId, canEdit = false }) => {
  const [entries, setEntries] = useState<CareerTimelineEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingEntry, setEditingEntry] = useState<CareerTimelineEntry | null>(null)
  const [form, setForm] = useState<FormState>({ ...BLANK })
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  useEffect(() => {
    if (!alumniId) return
    fetchEntries()
  }, [alumniId])

  const fetchEntries = async () => {
    setLoading(true)
    const { data, error } = await supabaseClient
      .from('career_timeline_entries')
      .select('*')
      .eq('alumni_id', alumniId)
      .order('start_date', { ascending: false })
    if (error) {
      console.error('[CareerTimeline] fetch error:', error)
    } else {
      setEntries(data || [])
    }
    setLoading(false)
  }

  const openAdd = () => {
    setEditingEntry(null)
    setForm({ ...BLANK })
    setShowForm(true)
  }

  const openEdit = (entry: CareerTimelineEntry) => {
    setEditingEntry(entry)
    setForm({
      entry_type: entry.entry_type,
      title: entry.title,
      organization: entry.organization || '',
      location: entry.location || '',
      start_date: entry.start_date,
      end_date: entry.end_date || '',
      is_current: entry.is_current,
      description: entry.description || '',
      employment_type: entry.employment_type || '',
      salary_range: entry.salary_range || '',
      degree: entry.degree || '',
      field_of_study: entry.field_of_study || '',
      credential_url: entry.credential_url || '',
    })
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingEntry(null)
    setForm({ ...BLANK })
  }

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return }
    if (!form.start_date)   { toast.error('Start date is required'); return }
    setSaving(true)
    const isJobType = ['job', 'internship'].includes(form.entry_type)
    const payload = {
      entry_type:      form.entry_type,
      title:           form.title.trim(),
      organization:    form.organization.trim() || null,
      location:        form.location.trim() || null,
      start_date:      form.start_date,
      end_date:        form.is_current ? null : (form.end_date || null),
      is_current:      form.is_current,
      description:     form.description.trim() || null,
      employment_type: isJobType ? (form.employment_type || null) : null,
      salary_range:    isJobType ? (form.salary_range || null) : null,
      degree:          form.entry_type === 'education' ? (form.degree.trim() || null) : null,
      field_of_study:  form.entry_type === 'education' ? (form.field_of_study.trim() || null) : null,
      credential_url:  ['certification', 'achievement'].includes(form.entry_type) ? (form.credential_url.trim() || null) : null,
    }
    if (editingEntry) {
      const { error } = await supabaseClient
        .from('career_timeline_entries')
        .update(payload)
        .eq('id', editingEntry.id)
      if (error) {
        toast.error('Failed to update entry')
        console.error(error)
      } else {
        toast.success('Entry updated')
        closeForm()
        fetchEntries()
      }
    } else {
      const { error } = await supabaseClient
        .from('career_timeline_entries')
        .insert({ ...payload, alumni_id: alumniId })
      if (error) {
        toast.error('Failed to add entry')
        console.error(error)
      } else {
        toast.success('Entry added')
        closeForm()
        fetchEntries()
      }
    }
    setSaving(false)
  }

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    const { error } = await supabaseClient
      .from('career_timeline_entries')
      .delete()
      .eq('id', id)
    if (error) {
      toast.error('Failed to delete entry')
      console.error(error)
    } else {
      toast.success('Entry removed')
      setEntries(prev => prev.filter(e => e.id !== id))
    }
    setDeletingId(null)
    setConfirmDeleteId(null)
  }

  const showJobFields  = ['job', 'internship'].includes(form.entry_type)
  const showEduFields  = form.entry_type === 'education'
  const showCredFields = ['certification', 'achievement'].includes(form.entry_type)

  /* ── Render ──────────────────────────────────────────────── */

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Section header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Briefcase className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Career Timeline</h2>
        </div>
        {canEdit && !showForm && (
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-4 h-4" /> Add Entry
          </button>
        )}
      </div>

      {/* Inline Add / Edit form */}
      {canEdit && showForm && (
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">{editingEntry ? 'Edit Entry' : 'New Entry'}</h3>
            <button onClick={closeForm} className="p-1 hover:bg-gray-200 rounded-lg transition">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Type */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={form.entry_type}
                onChange={e => setForm(f => ({ ...f, entry_type: e.target.value as CareerEntryType }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="job">Job</option>
                <option value="internship">Internship</option>
                <option value="education">Education</option>
                <option value="certification">Certification</option>
                <option value="achievement">Achievement</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Title */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {showEduFields ? 'Degree / Program' : 'Title'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder={showEduFields ? 'e.g., Bachelor of Science in Computer Science' : 'e.g., Software Engineer'}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Organization */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {showEduFields ? 'School / University' : 'Organization'}
              </label>
              <input
                type="text"
                value={form.organization}
                onChange={e => setForm(f => ({ ...f, organization: e.target.value }))}
                placeholder={showEduFields ? 'e.g., University of the Philippines' : 'e.g., Acme Corp'}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
              <input
                type="text"
                value={form.location}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder="e.g., Manila, Philippines"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Start date */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* End date + is_current */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                disabled={form.is_current}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
              />
              <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.is_current}
                  onChange={e => setForm(f => ({ ...f, is_current: e.target.checked, end_date: e.target.checked ? '' : f.end_date }))}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600"
                />
                <span className="text-xs text-gray-600">Currently here</span>
              </label>
            </div>

            {/* Job / Internship fields */}
            {showJobFields && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Employment Type</label>
                  <select
                    value={form.employment_type}
                    onChange={e => setForm(f => ({ ...f, employment_type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Not specified</option>
                    <option value="full_time">Full-time</option>
                    <option value="part_time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                    <option value="freelance">Freelance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Salary Range</label>
                  <select
                    value={form.salary_range}
                    onChange={e => setForm(f => ({ ...f, salary_range: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Not specified</option>
                    <option value="under_20k">Under ₱20,000</option>
                    <option value="20k_40k">₱20,000 – ₱40,000</option>
                    <option value="40k_60k">₱40,000 – ₱60,000</option>
                    <option value="60k_80k">₱60,000 – ₱80,000</option>
                    <option value="80k_100k">₱80,000 – ₱100,000</option>
                    <option value="over_100k">Over ₱100,000</option>
                  </select>
                </div>
              </>
            )}

            {/* Education fields */}
            {showEduFields && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Degree Level</label>
                  <input
                    type="text"
                    value={form.degree}
                    onChange={e => setForm(f => ({ ...f, degree: e.target.value }))}
                    placeholder="e.g., Bachelor of Science"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Field of Study</label>
                  <input
                    type="text"
                    value={form.field_of_study}
                    onChange={e => setForm(f => ({ ...f, field_of_study: e.target.value }))}
                    placeholder="e.g., Computer Science"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {/* Certification / Achievement fields */}
            {showCredFields && (
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Credential URL</label>
                <input
                  type="url"
                  value={form.credential_url}
                  onChange={e => setForm(f => ({ ...f, credential_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Description */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="Brief summary of this experience..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={closeForm}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {saving ? 'Saving…' : editingEntry ? 'Save Changes' : 'Add Entry'}
            </button>
          </div>
        </div>
      )}

      {/* Timeline entries */}
      {loading ? (
        <div className="py-6 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      ) : entries.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">
          {canEdit
            ? 'No entries yet. Click "Add Entry" to start building your career timeline.'
            : 'No career timeline entries yet.'}
        </div>
      ) : (
        <div className="relative">
          {/* Vertical connector line */}
          <div className="absolute left-[19px] top-5 bottom-5 w-0.5 bg-gray-200" />
          <div className="space-y-4">
            {entries.map(entry => {
              const colors = ENTRY_COLORS[entry.entry_type]
              const isDeleting = deletingId === entry.id
              const confirmingDelete = confirmDeleteId === entry.id
              return (
                <div key={entry.id} className="relative flex gap-4">
                  {/* Type icon dot */}
                  <div className={`relative z-10 w-10 h-10 rounded-full ${colors.dot} flex items-center justify-center shrink-0 text-white shadow-sm`}>
                    <EntryIcon type={entry.entry_type} />
                  </div>

                  {/* Entry card */}
                  <div className="flex-1 bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                            {ENTRY_TYPE_LABELS[entry.entry_type]}
                          </span>
                          {entry.is_current && (
                            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                              Current
                            </span>
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900 mt-1.5 text-sm leading-snug">{entry.title}</h4>
                        {entry.organization && (
                          <p className="text-sm text-gray-600 mt-0.5">{entry.organization}</p>
                        )}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDateRange(entry)}
                          </span>
                          {entry.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {entry.location}
                            </span>
                          )}
                        </div>

                        {/* Job / internship extras */}
                        {(entry.entry_type === 'job' || entry.entry_type === 'internship') && (
                          entry.employment_type || (entry.salary_range && canEdit)
                        ) && (
                          <div className="flex flex-wrap gap-2 mt-1.5 text-xs text-gray-500">
                            {entry.employment_type && (
                              <span>{EMP_TYPE_LABELS[entry.employment_type] || entry.employment_type}</span>
                            )}
                            {entry.salary_range && canEdit && (
                              <span className="text-gray-400">· {SALARY_LABELS[entry.salary_range] || entry.salary_range}</span>
                            )}
                          </div>
                        )}

                        {/* Education extras */}
                        {entry.entry_type === 'education' && (entry.degree || entry.field_of_study) && (
                          <p className="text-xs text-gray-500 mt-1.5">
                            {[entry.degree, entry.field_of_study].filter(Boolean).join(' in ')}
                          </p>
                        )}

                        {/* Credential link */}
                        {entry.credential_url && (
                          <a
                            href={entry.credential_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-1.5 transition"
                          >
                            <ExternalLink className="w-3 h-3" /> View Credential
                          </a>
                        )}

                        {/* Description */}
                        {entry.description && (
                          <p className="text-sm text-gray-600 mt-2 leading-relaxed">{entry.description}</p>
                        )}
                      </div>

                      {/* Edit / Delete actions */}
                      {canEdit && (
                        <div className="flex items-center gap-1 shrink-0">
                          {confirmingDelete ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-red-600 font-medium">Delete?</span>
                              <button
                                onClick={() => handleDelete(entry.id)}
                                disabled={isDeleting}
                                className="text-xs px-2 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition"
                              >
                                {isDeleting ? '…' : 'Yes'}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="text-xs px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => openEdit(entry)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                title="Edit"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(entry.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export { CareerTimeline }
export default CareerTimeline
